/**
 * DBNNigeriaMap.js — Nigeria project map using Leaflet
 * Projects colored by type: green, digital, green+digital, inclusion, general
 */
import React, { useState } from "react";
import SafeLeafletMap from "@/components/SafeLeafletMap";
import { TileLayer, CircleMarker, Popup, ZoomControl } from "react-leaflet";
import { Badge } from "./dbnUtils";

const TYPE_CONFIG = {
  green_digital: { color:"#34d399", fill:"#34d399", label:"Green & Digital" },
  green:         { color:"#22c55e", fill:"#22c55e", label:"Green" },
  digital:       { color:"#60a5fa", fill:"#60a5fa", label:"Digital" },
  inclusion:     { color:"#ec4899", fill:"#ec4899", label:"Inclusive Finance" },
  general:       { color:"#94a3b8", fill:"#94a3b8", label:"General MSME" },
};

const RISK_BORDER = {
  Low:      "#22c55e",
  Moderate: "#f59e0b",
  High:     "#ef4444",
};

export default function DBNNigeriaMap({ features = [], colorBy = "type", height = 380 }) {
  const [hovered, setHovered] = useState(null);

  const getColor = (f) => {
    if (colorBy === "type") return TYPE_CONFIG[f.type]?.color || "#94a3b8";
    if (colorBy === "risk") return RISK_BORDER[f.risk] || "#94a3b8";
    return "#34d399";
  };

  return (
    <div style={{ position: "relative" }}>
      <SafeLeafletMap
        center={[9.0820, 8.6753]}
        zoom={6}
        style={{ height, width: "100%", borderRadius: 10 }}
        wrapperStyle={{ borderRadius: 10, overflow: "hidden" }}
        zoomControl={false}
        attributionControl={false}>
        <ZoomControl position="bottomright" />
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          attribution='© OpenStreetMap © CARTO'
          maxZoom={18}
        />
        {features.map((f) => (
          <CircleMarker
            key={f.id}
            center={[f.lat, f.lng]}
            radius={f.finance > 3_000_000 ? 11 : f.finance > 1_000_000 ? 9 : 7}
            pathOptions={{
              fillColor: getColor(f),
              fillOpacity: 0.85,
              color: RISK_BORDER[f.risk] || "#fff",
              weight: f.risk === "High" ? 2.5 : 1.5,
            }}
            eventHandlers={{ mouseover: () => setHovered(f.id), mouseout: () => setHovered(null) }}>
            <Popup>
              <div style={{ minWidth: 180, fontFamily: "Arial, sans-serif" }}>
                <p style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>{f.name}</p>
                <p style={{ fontSize: 10, color: "#64748b", marginBottom: 6 }}>{f.sector} · {f.state}</p>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }}>
                  {f.type.includes("green") && <span style={{ fontSize: 9, background: "#14532d", color: "#34d399", borderRadius: 4, padding: "2px 5px" }}>Green</span>}
                  {f.type.includes("digital") && <span style={{ fontSize: 9, background: "#1e3a5f", color: "#60a5fa", borderRadius: 4, padding: "2px 5px" }}>Digital</span>}
                </div>
                <p style={{ fontSize: 10 }}>Finance: <strong>€{(f.finance/1000000).toFixed(1)}M</strong></p>
                <p style={{ fontSize: 10 }}>Jobs: <strong>{f.jobs?.toLocaleString()}</strong></p>
                {f.emissions > 0 && <p style={{ fontSize: 10 }}>CO₂e: <strong>{Math.round(f.emissions).toLocaleString()} t</strong></p>}
                <p style={{ fontSize: 9, color: f.risk === "Low" ? "#22c55e" : f.risk === "High" ? "#ef4444" : "#f59e0b", marginTop: 4 }}>
                  ● Risk: {f.risk}
                </p>
              </div>
            </Popup>
          </CircleMarker>
        ))}
      </SafeLeafletMap>

      {/* Legend */}
      <div style={{
        position: "absolute", bottom: 12, left: 12, zIndex: 900,
        background: "rgba(7,17,30,0.92)", border: "1px solid #0f2033",
        borderRadius: 8, padding: "8px 10px",
      }}>
        {Object.entries(TYPE_CONFIG).map(([k, v]) => (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
            <div style={{ width: 9, height: 9, borderRadius: "50%", background: v.color }} />
            <span style={{ fontSize: 9, color: "#94a3b8" }}>{v.label}</span>
          </div>
        ))}
        <div style={{ height: 1, background: "#0f2033", margin: "5px 0" }} />
        <p style={{ fontSize: 8, color: "#334155" }}>Border = Risk level</p>
      </div>
    </div>
  );
}
